import AccordionCustom from "@components/Accordion/AccordionCustom";
import ImageGrid from "@components/ImageGrid";
import CardDataCarousal from "@components/LandingPageComponents/CardDataCarousal";
import { CardDataDetailsProps } from "@components/LandingPageComponents/CardDataDetails";
import FeatureCardSection from "@components/LandingPageComponents/CardSection";
import GoogleSheetHeader from "@components/LandingPageComponents/google-sheets-components/GoogleSheetHeader";
import FaqHeader from "@components/LandingPageComponents/RazorPayComponents/FaqHeader";
import TestimonialSection from "@components/LandingPageComponents/TestimonialSection";
import ClientLogos from "@components/new-ui/ClientLogos";
import NewLayout from "@components/new-ui/Layout";
import YellowCTAhref from "@components/yellowCTAhref";
import Head from "next/head";
import React from "react";

const cardDataCollection: CardDataDetailsProps[] = [
    {
        img: "https://assets.gallabox.com/gb-home/g-sheet/googlesheet-icon1.png",
        title: "Import contacts from Google Sheets in seconds",
        description: "Connect your Google Sheet to Galabox with one simple click, and import contacts instantly to run campaigns on WhatsApp.",
    },
    {
        img: "https://assets.gallabox.com/gb-home/hubspot-send-personalized.svg",
        title: "Send personalized WhatsApp messages",
        description:
            "Have prospect lists, customer feedback, appointments, or orders on Google Sheets? Send personalized messages, reminders and notifications as part of any workflow with just a few clicks.",
    },
    {
        img: "https://assets.gallabox.com/gb-home/g-sheet/googlesheet-icon3.png",
        title: "Share product and service catalogs instantly",
        description:
            "Reach out to your customers and prospects on Google Sheets, and share product and service catalogs on WhatsApp. Automate your upselling and cross-selling process.",
    },
    {
        img: "https://assets.gallabox.com/gb-home/hubspot-track-customer.svg",
        title: "Trigger event-based messages and track engagement",
        description:
            "Automatically trigger messages within your Google Sheets whenever a new contact is added.Track messages, sends, opens, clicks so that no opportunity falls through the cracks.",
    },
];

const faqPageSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
        {
            "@type": "Question",
            name: "Do I need coding knowledge for Google Sheets WhatsApp Integration?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "No! You can integrate HubSpot and WhatsApp in minutes with just a few clicks. In no time, you can send personalized WhatsApp messages and streamline communication for all your HubSpot contacts.",
            },
        },
        {
            "@type": "Question",
            name: "What does WhatsApp for Google Sheets mean to you?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "The Google Sheets and WhatsApp Integration enabled by Gallabox allows you to automate sending personalized WhatsApp messages at scale and streamline the spreadsheet data management and communication processes.",
            },
        },
        {
            "@type": "Question",
            name: "What process can I streamline with WhatsApp Google Sheet automation?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "You can automate several manual spreadsheet-based tasks such as updating contact information, managing orders, tracking inventory, and more, thus improving overall communication efficiency.",
            },
        },
        {
            "@type": "Question",
            name: "Can I customize WhatsApp messages for Google Sheets users?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Absolutely! You can send custom messages to every contact on Google Sheets and also configure triggers every time a new contact gets added to the spreadsheet.",
            },
        },
    ],
};

const GoogleSheetsLanding: React.FC = () => {
    return (
        <NewLayout
            title="WhatsApp Integration with Google Sheets | Gallabox"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/g-sheet/googlesheet-og.png" }}
            canonical="https://gallabox.com/whatsapp-integrations/google-sheets"
            description="Send WhatsApp messages directly from Sheets with WhatsApp integration with Google Sheets. Automate data-driven chats."
        >
            <Head>
                <script type="application/ld+json">{JSON.stringify(faqPageSchema)}</script>
            </Head>
            <GoogleSheetHeader />

            <div className="lg:max-w-7xl mx-auto">
                <h3 className="text-base text-center font-normal py-2">Trusted by 10,000+ businesses</h3>
                <ClientLogos />
            </div>
            <CardDataCarousal cardDataCollection={cardDataCollection} />
            <FeatureCardSection
                subTitle="No code setup"
                title="Run campaigns for Google Sheets contacts with ease"
                imageURl="https://assets.gallabox.com/gb-home/g-sheet/googlesheet-no code.png"
                description="Streamline your WhatsApp customer engagement workflow for your contacts on Google Sheets. The predefined templates with module fields help you reach prospects and customers with the right message everytime a new row gets added. Save time and improve your productivity."
                isImageRightSide
            />
            <FeatureCardSection
                subTitle="Broadcast Campaigns"
                imageURl="https://assets.gallabox.com/gb-home/g-sheet/googlesheet-broad.png"
                title="Broadcast personalized WhatsApp campaigns at scale"
                description="Run focused campaigns for your contacts on Google Sheets with open rates north of 90% with recommended products, new launches, and more. Get real-time analytics to take action on time, every time."
            />
            <FeatureCardSection
                imageURl="https://assets.gallabox.com/gb-home/googlesheet/google-card3.png"
                subTitle="Bot Connector"
                title="Instantly save customer responses on Google Sheets"
                description="Integrating Google Sheets with Gallabox means no more toggling between spreadsheets and the app. Run surveys or feedback campaigns using bots and instantly save the customer responses on Google Sheets so that no responses slip through the cracks."
                isImageRightSide
            />
            <FeatureCardSection
                imageURl="https://assets.gallabox.com/gb-home/googlesheet/google-card4.png"
                subTitle="Contact Assignment"
                title="Automatically assign leads to reps on the go"
                description="No more losing opportunities due to late customer reach out. With Gallabox’s integration with Google Sheets, you can automatically assign the right leads to the right sales reps or queries to the right customer support agents based on parameters like location, timezone, and more."
            />
            <YellowCTAhref
                customText={<h2 className="text-2xl md:text-3xl font-bold text-yellow-800">Start Sending WhatsApp messages from Google Sheets</h2>}
                // link2="https://calendly.com/gallabox/get-personalized-walktrhough?utm_source=website&utm_medium=googel-sheets&utm_campaign=lp"
            />
            <TestimonialSection
                TestimonialSectionPropsData={[
                    {
                        companyImg: "https://assets.gallabox.com/gb-home/g-sheet/googlesheet-logo2.png",
                        avatarImg: "https://assets.gallabox.com/gb-home/g-sheet/googlesheet-owner2.png",
                        headerTitle: "Gallabox’s WhatsApp Google Sheet integration. Effortlessly manages data, enhancing efficiency and accuracy.",
                        nameText: "Matasim A.",
                        professionText: "Founder, Icomex",
                    },
                    {
                        companyImg: "https://assets.gallabox.com/gb-home/g-sheet/googlesheet-comp1.png",
                        avatarImg: "https://assets.gallabox.com/gb-home/g-sheet/googlesheet-owner1.png",
                        headerTitle: "Even though I’m not a tech expert, I was able to set up the integration without any hassle.",
                        nameText: "Harish",
                        professionText: "Founder, E Panipuri Cart",
                    },
                ]}
            />
            <ImageGrid />
            <YellowCTAhref
                customText={<h2 className="text-2xl md:text-3xl font-bold text-yellow-800">Start Connecting with your customers on WhatsApp from Google Sheets!</h2>}
                // link2="https://calendly.com/gallabox/get-personalized-walktrhough?utm_source=website&utm_medium=googel-sheets&utm_campaign=lp"
            />
            <FaqHeader description="Everything you need to know about the Google Sheets-Gallabox integration" />
            <AccordionCustom
                title="Google Sheets Integration"
                items={[
                    {
                        question: "Do I need coding knowledge for Google Sheets WhatsApp Integration?",
                        answer: "Not at all. This is where Gallabox makes it super easy. With just a few clicks, you can integrate Google Sheets and WhatsApp in seconds and start your WhatsApp campaigns.",
                    },
                    {
                        question: "What does WhatsApp for Google Sheets mean to you?",
                        answer: "The Google Sheets and WhatsApp Integration enabled by Gallabox allows you to automate sending personalized WhatsApp messages at scale and streamline the spreadsheet data management and communication processes.",
                    },
                    {
                        question: "What process can I streamline with WhatsApp Google Sheet automation?",
                        answer: "You can automate several manual spreadsheet-based tasks such as updating contact information, managing orders, tracking inventory, and more, thus improving overall communication efficiency.",
                    },
                    {
                        question: "Can I customize WhatsApp messages for Google Sheets users?",
                        answer: "Absolutely! You can send custom messages to every contact on Google Sheets and also configure triggers every time a new contact gets added to the spreadsheet.",
                    },
                ]}
            />
        </NewLayout>
    );
};

export default GoogleSheetsLanding;
